// Student Editor - PartBPage7 - Complete version with all sections visible
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartBPage7 = () => {
  const { activity } = useActivityContext();
  const [learnerPerf, setLearnerPerf] = useState({ awareness_performance_level: '', sensitivity_performance_level: '', creativity_performance_level: '' });
  const [peerPerf, setPeerPerf] = useState({ awareness_performance_level: '', sensitivity_performance_level: '', creativity_performance_level: '' });
  const [saveMessage, setSaveMessage] = useState('');

  // Calculate totals from previous pages
  const calcTotals = () => {
    const myResp = activity?.my_response || {};

    const teacherStage1 = {
      awareness: activity?.part_b?.page_3?.stage1?.awareness?.length || 0,
      sensitivity: activity?.part_b?.page_3?.stage1?.sensitivity?.length || 0,
      creativity: activity?.part_b?.page_3?.stage1?.creativity?.length || 0
    };
    const teacherStage2 = {
      awareness: activity?.part_b?.page_4?.stage2?.awareness?.length || 0,
      sensitivity: activity?.part_b?.page_4?.stage2?.sensitivity?.length || 0,
      creativity: activity?.part_b?.page_4?.stage2?.creativity?.length || 0
    };
    const teacherStage3Rubric = activity?.part_b?.page_6?.rubric || {};
    const teacherStage3 = {
      awareness: teacherStage3Rubric.awareness_level === 'beginner' ? 5 : teacherStage3Rubric.awareness_level === 'proficient' ? 10 : teacherStage3Rubric.awareness_level === 'advanced' ? 15 : 0,
      sensitivity: teacherStage3Rubric.sensitivity_level === 'beginner' ? 5 : teacherStage3Rubric.sensitivity_level === 'proficient' ? 10 : teacherStage3Rubric.sensitivity_level === 'advanced' ? 15 : 0,
      creativity: teacherStage3Rubric.creativity_level === 'beginner' ? 5 : teacherStage3Rubric.creativity_level === 'proficient' ? 10 : teacherStage3Rubric.creativity_level === 'advanced' ? 15 : 0
    };

    const learnerStage1 = {
      awareness: myResp?.page_2?.awareness ? Object.values(myResp.page_2.awareness).filter(Boolean).length : 0,
      sensitivity: myResp?.page_2?.sensitivity ? Object.values(myResp.page_2.sensitivity).filter(Boolean).length : 0,
      creativity: myResp?.page_2?.creativity ? Object.values(myResp.page_2.creativity).filter(Boolean).length : 0
    };
    const learnerStage3 = {
      awareness: myResp?.page_5?.learner_reflection?.awareness?.length || 0,
      sensitivity: myResp?.page_5?.learner_reflection?.sensitivity?.length || 0,
      creativity: myResp?.page_5?.learner_reflection?.creativity?.length || 0
    };

    const peerStage3 = {
      awareness: myResp?.page_5?.peer_feedback?.awareness?.length || 0,
      sensitivity: myResp?.page_5?.peer_feedback?.sensitivity?.length || 0,
      creativity: myResp?.page_5?.peer_feedback?.creativity?.length || 0
    };

    return {
      teacher: {
        stage1: teacherStage1,
        stage2: teacherStage2,
        stage3: teacherStage3,
        total: {
          awareness: teacherStage1.awareness + teacherStage2.awareness + teacherStage3.awareness,
          sensitivity: teacherStage1.sensitivity + teacherStage2.sensitivity + teacherStage3.sensitivity,
          creativity: teacherStage1.creativity + teacherStage2.creativity + teacherStage3.creativity
        }
      },
      learner: {
        stage1: learnerStage1,
        stage3: learnerStage3,
        total: {
          awareness: learnerStage1.awareness + learnerStage3.awareness,
          sensitivity: learnerStage1.sensitivity + learnerStage3.sensitivity,
          creativity: learnerStage1.creativity + learnerStage3.creativity
        }
      },
      peer: { stage3: peerStage3 }
    };
  };

  const totals = calcTotals();
  const teacherPerf = activity?.part_b?.page_7?.teacher_assessment || {};

  useEffect(() => {
    if (activity?.my_response?.page_7) {
      setLearnerPerf(activity.my_response.page_7.learner_reflection || {});
      setPeerPerf(activity.my_response.page_7.peer_feedback || {});
    }
  }, [activity]);

  const saveChanges = async (section, data) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-b/page/7/student-fields`,
        {
          section: section, // 'learner_reflection' or 'peer_feedback'
          awareness_performance_level: data.awareness_performance_level,
          sensitivity_performance_level: data.sensitivity_performance_level,
          creativity_performance_level: data.creativity_performance_level
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleLearnerChange = (ability, level) => {
    const newData = { ...learnerPerf, [`${ability}_performance_level`]: level };
    setLearnerPerf(newData);
    saveChanges('learner_reflection', newData);
  };

  const handlePeerChange = (ability, level) => {
    const newData = { ...peerPerf, [`${ability}_performance_level`]: level };
    setPeerPerf(newData);
    saveChanges('peer_feedback', newData);
  };

  const renderTable = (title, stageData, bgColor) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '6px' }}>
      <thead>
        <tr style={{ backgroundColor: bgColor }}>
          <th style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', fontWeight: 'bold', textAlign: 'center' }}>{title}</th>
          <th style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', fontWeight: 'bold' }}>Awareness</th>
          <th style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', fontWeight: 'bold' }}>Sensitivity</th>
          <th style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', fontWeight: 'bold' }}>Creativity</th>
        </tr>
      </thead>
      <tbody>
        {stageData.map((row, idx) => (
          <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? (bgColor === '#FFD700' ? '#FFF9E6' : bgColor === '#6FA8DC' ? '#C5E3ED' : '#FFF4CC') : 'white' }}>
            <td style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', fontWeight: row.label === 'Total' ? 'bold' : 'normal', textAlign: 'center' }}>{row.label}</td>
            <td style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', textAlign: 'center' }}>{row.data.awareness}</td>
            <td style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', textAlign: 'center' }}>{row.data.sensitivity}</td>
            <td style={{ border: '2px solid #333', padding: '4px', fontSize: '14px', textAlign: 'center' }}>{row.data.creativity}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderPerformanceCheckboxes = (ability, selectedLevel, onChange, editable) => {
    const levels = [
      { key: 'beginner', label: 'Beginner', range: editable ? '(5-13)' : '(0-7)' },
      { key: 'proficient', label: 'Proficient', range: editable ? '(12-18)' : '(8)' },
      { key: 'advanced', label: 'Advanced', range: editable ? '(19-20)' : '(9)' }
    ];

    return (
      <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
        <div style={{
          backgroundColor: '#3F51B5',
          color: 'white',
          padding: '6px 16px',
          fontWeight: 'bold',
          fontSize: '14px',
          minWidth: '90px',
          textAlign: 'center',
          textTransform: 'capitalize'
        }}>
          {ability}
        </div>
        {levels.map(level => (
          <div key={level.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
            <div style={{
              backgroundColor: level.key === selectedLevel ? '#FFD700' : 'white',
              border: '2px solid #333',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '15px',
              fontWeight: 'bold',
              textAlign: 'center',
              minWidth: '60px'
            }}>
              {level.label}<br />{level.range}
            </div>
            {editable ? (
              <input
                type="checkbox"
                checked={level.key === selectedLevel}
                onChange={() => onChange(ability, level.key)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
            ) : (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #999',
                backgroundColor: '#f9f9f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '15px'
              }}>
                {level.key === selectedLevel && '✓'}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ width: '100%',
        maxWidth: '1400px',
        minHeight: '80vh',
        margin: '0 auto',
        padding: '40px', backgroundColor: 'white',
        fontFamily: "'Poppins', Arial, sans-serif", boxShadow: '0 4px 20px rgba(0,0,0,0.08)', position: 'relative' }}>
        {saveMessage && <div style={{ position: 'fixed', top: '20px', right: '20px', backgroundColor: saveMessage.includes('Error') ? '#f44336' : '#4caf50', color: 'white', padding: '8px 16px', borderRadius: '4px', zIndex: 1000 }}>{saveMessage}</div>}
        

        <div style={{ backgroundColor: '#F4824C', color: 'white', fontSize: '14px', fontWeight: 'bold', padding: '10px', textAlign: 'center', marginBottom: '6px' }}>Overview</div>
        <div style={{ backgroundColor: '#F4824C', color: 'white', fontSize: '14px', padding: '6px 10px', textAlign: 'center', marginBottom: '16px' }}>
          Tabulate all the feedback for the group project on this page.
        </div>

        {/* Teacher Assessment - View Only */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ backgroundColor: '#F4824C', color: 'white', padding: '6px 10px', fontWeight: 'bold', fontSize: '14px', marginBottom: '6px' }}>Teacher Assessment</div>
          <div style={{ fontSize: '15px', marginBottom: '4px', lineHeight: '1.4' }}>
            <strong>1.</strong> For <strong>Stages 1</strong> and <strong>2</strong>, write the <strong>total</strong> number of statements you ticked.
            &nbsp;&nbsp;<strong>3.</strong> Tick the <strong>final performance level</strong> based on the final score.
          </div>
          <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: '1.4' }}>
            For <strong>Stage 3</strong>, write the numeric value that corresponds to the <strong>achievement level</strong> (beginner, proficient, advanced) for <strong>each ability</strong> (awareness, sensitivity, creativity) you chose for the final product.
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {renderTable('Teacher', [
                { label: 'Stage 1', data: totals.teacher.stage1 },
                { label: 'Stage 2', data: totals.teacher.stage2 },
                { label: 'Stage 3', data: totals.teacher.stage3 },
                { label: 'Total', data: totals.teacher.total }
              ], '#FFD700')}
              <div style={{ fontSize: '15px', marginTop: '4px' }}><strong>2.</strong> Add the total here. ↗</div>
            </div>
            <div style={{ flex: 0.8 }}>
              {renderPerformanceCheckboxes('awareness', teacherPerf.awareness_performance_level, null, false)}
              {renderPerformanceCheckboxes('sensitivity', teacherPerf.sensitivity_performance_level, null, false)}
              {renderPerformanceCheckboxes('creativity', teacherPerf.creativity_performance_level, null, false)}
            </div>
          </div>
        </div>

        {/* Learner Reflection - Editable */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ backgroundColor: '#4A90A4', color: 'white', padding: '6px 10px', fontWeight: 'bold', fontSize: '14px', marginBottom: '6px' }}>Learner Reflection</div>
          <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: '1.4' }}>
            <strong>1.</strong> For <strong>Stages 1</strong> and <strong>3</strong>, write the <strong>total</strong> number of statements the learner has ticked (✓).
            &nbsp;&nbsp;<strong>3.</strong> Tick the <strong>final performance level</strong> based on the final score.
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {renderTable('Learner', [
                { label: 'Stage 1', data: totals.learner.stage1 },
                { label: 'Stage 3', data: totals.learner.stage3 },
                { label: 'Total', data: totals.learner.total }
              ], '#6FA8DC')}
              <div style={{ fontSize: '15px', marginTop: '4px' }}><strong>2.</strong> Add the total here. ↗</div>
            </div>
            <div style={{ flex: 0.8 }}>
              {renderPerformanceCheckboxes('awareness', learnerPerf.awareness_performance_level, handleLearnerChange, true)}
              {renderPerformanceCheckboxes('sensitivity', learnerPerf.sensitivity_performance_level, handleLearnerChange, true)}
              {renderPerformanceCheckboxes('creativity', learnerPerf.creativity_performance_level, handleLearnerChange, true)}
            </div>
          </div>
        </div>

        {/* Peer Feedback - Editable */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ backgroundColor: '#DAA520', color: 'white', padding: '6px 10px', fontWeight: 'bold', fontSize: '14px', marginBottom: '6px' }}>Peer Feedback</div>
          <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: '1.4' }}>
            <strong>1.</strong> For <strong>Stage 3</strong>, write the <strong>total</strong> number of statements the peer has ticked (✓).
            &nbsp;&nbsp;<strong>2.</strong> Tick the <strong>final performance level</strong> based on the score.
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {renderTable('Peer', [
                { label: 'Stage 3', data: totals.peer.stage3 }
              ], '#F4D03F')}
            </div>
            <div style={{ flex: 0.8 }}>
              {renderPerformanceCheckboxes('awareness', peerPerf.awareness_performance_level, handlePeerChange, true)}
              {renderPerformanceCheckboxes('sensitivity', peerPerf.sensitivity_performance_level, handlePeerChange, true)}
              {renderPerformanceCheckboxes('creativity', peerPerf.creativity_performance_level, handlePeerChange, true)}
            </div>
          </div>
        </div>

        
        
      </div>

  );
};

export default PartBPage7;
